import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { env } from '../../lib/env';
import { supabase } from '../../lib/supabase';
import { formatMegabytes, resolveAudioType } from '../lib/audio-file';

// Listening audio lives in the private `listening-audio` bucket. Staff upload
// directly (bucket-wide INSERT policy); the exam player mints a short-lived
// signed URL to play it back.
//
// Phase 122: a WHOLE-TEST recording is the common teacher upload and a
// 30-minute MP3 runs 28–45 MB, so the old 25 MB client cap rejected exactly
// the file the Listening builder is designed around while four per-part files
// (~7 MB each) sailed through. The cap now matches the bucket's, the type
// check is lenient (see ../lib/audio-file), and the upload reports progress —
// a 45 MB POST with no feedback is indistinguishable from a hang.

const BUCKET = 'listening-audio';
const MAX_BYTES = 100 * 1024 * 1024; // 100 MB — matches the bucket's file_size_limit.

/** Ceiling on the `<audio>` metadata probe in `readAudioDuration`. Local files
 *  resolve in milliseconds; this only exists so a probe that never fires cannot
 *  block an upload behind it. */
const METADATA_TIMEOUT_MS = 10_000;

export interface UploadAudioArgs {
  /** The path inside the bucket, e.g. `drafts/<draft_id>/<uuid>-test-audio.mp3`. */
  path: string;
  file: File;
}

export interface UploadAudioResult {
  path: string;
  bytes: number;
  mime: string;
}

export class AudioUploadError extends Error {
  constructor(
    message: string,
    readonly code: 'invalid_type' | 'too_large' | 'upload_failed',
  ) {
    super(message);
    this.name = 'AudioUploadError';
  }
}

/**
 * Upload with byte-level progress.
 *
 * supabase-js's `storage.upload()` uses `fetch`, which cannot report request
 * progress, so we POST to the same Storage endpoint with XMLHttpRequest —
 * the one browser API that exposes `upload.onprogress`. Everything else is
 * identical: same URL, same auth (the caller's JWT, so the bucket's RLS
 * INSERT policy still decides), same headers supabase-js sends.
 *
 * Deliberately NOT a resumable/TUS upload: tus-js-client pulls seven
 * transitive dependencies (including `url-parse`) for resumability that a
 * single retry button covers just as well at these file sizes.
 */
async function uploadWithProgress(
  path: string,
  file: File,
  contentType: string,
  onProgress: (percent: number) => void,
): Promise<void> {
  const { data } = await supabase.auth.getSession();
  const accessToken = data.session?.access_token;
  if (accessToken === undefined) {
    throw new AudioUploadError('Your session has expired — sign in again.', 'upload_failed');
  }

  const endpoint = `${env.SUPABASE_URL}/storage/v1/object/${BUCKET}/${path
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/')}`;

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', endpoint, true);
    xhr.setRequestHeader('authorization', `Bearer ${accessToken}`);
    xhr.setRequestHeader('apikey', env.SUPABASE_PUBLISHABLE_KEY);
    xhr.setRequestHeader('content-type', contentType);
    xhr.setRequestHeader('cache-control', 'max-age=3600');
    xhr.setRequestHeader('x-upsert', 'true');

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) {
        onProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100);
        resolve();
        return;
      }
      // Storage errors come back as JSON; show the server's own words (e.g.
      // "The object exceeded the maximum allowed size") rather than a code.
      let message = `Upload failed (HTTP ${String(xhr.status)}).`;
      try {
        const body: unknown = JSON.parse(xhr.responseText);
        if (typeof body === 'object' && body !== null) {
          const detail = (body as Record<string, unknown>).message;
          if (typeof detail === 'string' && detail !== '') message = detail;
        }
      } catch {
        // Non-JSON body — keep the status-code message.
      }
      reject(new AudioUploadError(message, 'upload_failed'));
    };
    xhr.onerror = () =>
      reject(
        new AudioUploadError(
          'The upload was interrupted — check your connection and try again.',
          'upload_failed',
        ),
      );
    xhr.onabort = () => reject(new AudioUploadError('The upload was cancelled.', 'upload_failed'));

    xhr.send(file);
  });
}

export function useUploadAudio(): {
  upload: (args: UploadAudioArgs) => Promise<UploadAudioResult>;
  isPending: boolean;
  /** 0–100 while a file is in flight; null when idle. */
  progress: number | null;
  error: Error | null;
  reset: () => void;
} {
  const [error, setError] = useState<Error | null>(null);
  const [progress, setProgress] = useState<number | null>(null);

  const mutation = useMutation({
    mutationFn: async ({ path, file }: UploadAudioArgs): Promise<UploadAudioResult> => {
      const contentType = resolveAudioType(file);
      if (contentType === null) {
        const seen = file.type === '' ? '' : ` (${file.type})`;
        throw new AudioUploadError(
          `"${file.name}" is not an audio file${seen}. Use MP3, M4A/AAC, WAV, OGG or FLAC.`,
          'invalid_type',
        );
      }
      if (file.size > MAX_BYTES) {
        throw new AudioUploadError(
          `"${file.name}" is ${formatMegabytes(file.size)} MB; the limit is ${String(MAX_BYTES / 1024 / 1024)} MB. Re-export it as a 128 kbps MP3.`,
          'too_large',
        );
      }

      setProgress(0);
      await uploadWithProgress(path, file, contentType, setProgress);
      return { path, bytes: file.size, mime: contentType };
    },
    onError: (err: Error) => {
      setError(err);
      setProgress(null);
    },
    onSuccess: () => {
      setError(null);
      setProgress(null);
    },
  });

  const { mutateAsync, reset: resetMutation } = mutation;
  const reset = useCallback(() => {
    setError(null);
    setProgress(null);
    resetMutation();
  }, [resetMutation]);

  return { upload: mutateAsync, isPending: mutation.isPending, progress, error, reset };
}

/**
 * Read an audio source's duration in whole seconds without playing it — a
 * detached `<audio>` element loading metadata only. `source` is a File (read
 * via a temporary object URL) or an already-signed URL. Resolves null when
 * the metadata cannot be read (corrupt file, unsupported codec, live stream);
 * callers treat null as "unknown", never as an error.
 */
export function readAudioDuration(source: File | string): Promise<number | null> {
  return new Promise((resolve) => {
    const objectUrl = typeof source === 'string' ? null : URL.createObjectURL(source);
    const el = document.createElement('audio');
    el.preload = 'metadata';
    let settled = false;
    const done = (value: number | null): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
      el.removeAttribute('src');
      resolve(value);
    };
    // NEITHER event is guaranteed to fire: a codec the browser silently
    // refuses, or a stalled network fetch for a signed URL, leaves both
    // handlers pending forever. Since Phase 131 this promise GATES the upload,
    // so a hang would strand the teacher's file with no error at all — the
    // worst possible failure. Time out into the documented "unknown" instead.
    const timer = setTimeout(() => done(null), METADATA_TIMEOUT_MS);
    el.onloadedmetadata = () => {
      const seconds = el.duration;
      done(Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : null);
    };
    el.onerror = () => done(null);
    el.src = objectUrl ?? (source as string);
  });
}

/**
 * Returns a short-lived signed URL for a path in the `listening-audio` bucket.
 * Used by SectionEditorPage's preview player + (Phase 16) by the live test
 * Listening section.
 */
export async function getListeningAudioSignedUrl(
  path: string,
  expiresInSeconds = 60 * 60,
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, expiresInSeconds);
  if (error || !data) {
    throw new Error(`Failed to sign listening audio URL: ${error?.message ?? 'no url'}`);
  }
  return data.signedUrl;
}
